import {
  Box,
  Container,
  Flex,
  Stack,
  Image,
  Link,
  Text,
} from '@chakra-ui/react';
import isMobileDevice from 'ismobilejs';
import type { GetServerSideProps, NextPage } from 'next';
import React from 'react';
import { SPECIALS } from '../../../common/constants';
import { ArticleStatus, shouldNotShow } from '../../../common/libs/article';
import { DiscloseScope } from '../../../common/libs/discloseScope';
import { sendAnalyticsEvent } from '../../../common/libs/googleAnalytics';
import { hasDemoAuthority } from '../../../common/libs/userRoleType';
import ArticlesGrid, { ArticleHeader } from '../../../components/ArticlesGrid';
import { tagsString } from '../../../common/libs/tag';
import * as date from '../../../server/libs/date';
import { getGaCid } from '../../../server/libs/googleAnalytics';
import { fetchAccountIntegrationProfiles } from '../../../server/libs/id';
import prisma from '../../../server/libs/prisma';
import withAuth from '../../../server/libs/withAuth';
import { Tag } from '../../tagsearch/[tagIds]';
import createUserAccessHistory from '../../../server/libs/createUserAccessHistory';
import { AntaaAccountType } from '../../../common/libs/antaaAccountType';
import FullWidthBox from '../../../components/FullWidthBox';
import { getSpecialArticleProps } from '../../../server/libs/specialArticles';
import {
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isLicenseVerified,
} from '../../../common/libs/user';
import useSpecialPage from '../../../hooks/useSpecialPage';
import {
  findTagsByNames,
  getArticleIdsWithTagIds,
} from '../../../server/libs/tag';

export const setting = {
  specialPageSlug: 'clinical-reasoning',
  specialPageShortName: 'cReason',
  tagTexts: SPECIALS.clinicalReasoning.tags,
  specialPagePath: SPECIALS.clinicalReasoning.link,
  specialPageTitle: SPECIALS.clinicalReasoning.alt,
};

const mainImagePath = SPECIALS.clinicalReasoning.mainImage ?? '';
const userAccessHistoryOperation = `special_${setting.specialPageShortName}`; // user_access_histories.operationカラムの値のため合計16文字以内
const specialPageSlug = setting.specialPageSlug;

const HeroContentBgColor = '#618FB4';

const title = (
  <Flex
    px={8}
    color="white"
    justifyContent="center"
    alignItems="center"
    gap={{
      base: 2,
      md: 7,
    }}
    flexDirection={{ base: 'column', md: 'row' }}
  >
    <Flex
      textAlign="center"
      flexDirection="column"
      alignItems="center"
      pt={{
        base: 8,
        md: 0,
      }}
    >
      <Text
        fontSize={{
          base: 60,
          md: 87,
        }}
        lineHeight={1}
      >
        診断推論
      </Text>
      <Text
        fontSize={{
          base: 40,
          md: 50,
        }}
        lineHeight={1.8}
      >
        特集
      </Text>
      <Image
        pt={5}
        src="/images/logo_corp_white.svg"
        alt="antaaロゴ"
        width={{
          base: 83,
          md: 111,
        }}
      />
    </Flex>
    <Image
      pt={{
        base: 2,
        md: 24,
        xl: 14,
      }}
      src="/images/special/clinical-reasoning_head_illust.svg"
      alt="診断推論特集"
      width={{ base: 283, xl: 368 }}
      maxW="100%"
    />
  </Flex>
);

const description = (
  <Stack textAlign="center" py={10}>
    <Text fontSize="xl" fontWeight="bold" mb={2}>
      企画概要
    </Text>
    <Text fontSize="sm">
      本特集ページ「診断推論」は、医療従事者および医学生を対象に、診断推論に関する最新の理論、実践法、ケーススタディを提供することを目的としています。基礎から応用まで幅広い内容をカバーし、臨床現場での実践に役立つ知識とスキルを体系的に学ぶことができます。また、実際の症例を通じて診断推論能力を向上させ、患者さんへの最適なケアを実現するための参考資料として活用できます。
    </Text>
  </Stack>
);

const bannerListTitle = (
  <Stack>
    <Box>
      <Text fontSize="xl" fontWeight="bold">
        疾患解説
      </Text>
      <Text fontSize="xs">
        希少疾患に関する詳細な情報を提供します。原因、症状、診断基準、治療法など各疾患について深く理解するための参考資料です。
      </Text>
    </Box>
    <Box>
      <Text fontSize="md" fontWeight="bold" display="inline-block">
        HAEとは
      </Text>
      <Text fontSize="xs" display="inline">
        ※情報提供：武田薬品工業株式会社　下記バナーは腫れ腹痛ナビPROへ遷移します
      </Text>
    </Box>
  </Stack>
);

const articleIdList: string[] = [];

const bannerSettings: {
  gaLabel: string;
  alt: string;
  image: string;
  link: string;
}[] = [
  {
    gaLabel: 'clinical_reasoning_banner_rare_disease',
    alt: '希少疾患のイメージ',
    image: '/images/special/clinical-reasoning-banner_rare-disease.png',
    link: 'https://www.takedamed.com/health/rare-disease_hae',
  },
  {
    gaLabel: 'clinical_reasoning_banner_check_sheet',
    alt: 'HAEチェックシートを試す',
    image: '/images/special/clinical-reasoning-banner_check-sheet.jpg',
    link: 'https://www.takedamed.com/health/genetic_diseases/hae-pro/inspection-diagnosis/check_sheet',
  },
  {
    gaLabel: 'clinical_reasoning_banner_hospital_search',
    alt: '病院を探す',
    image: '/images/special/clinical-reasoning-banner_hospital-search.jpg',
    link: 'https://takedamed.com/health/rare-disease_hae/inspection-diagnosis/hospital_search',
  },
];

const banners = (
  <Flex
    flexDir={{
      base: 'column',
      md: 'row',
    }}
    gap={4}
    alignItems="center"
  >
    {bannerSettings.map(({ gaLabel, alt, image, link }) => (
      <Link
        key={alt}
        isExternal
        onClick={() => sendAnalyticsEvent('special_contents', 'click', gaLabel)}
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        flexGrow={1}
        _hover={{
          opacity: '0.8',
        }}
        maxW={{
          base: 'none',
          md: 'calc((100% - 16px * 2) * 0.47)', // 1番目の横長のバナーが収まるように調整した値
        }}
      >
        <Image src={image} alt={alt} objectFit="contain" />
      </Link>
    ))}
  </Flex>
);

// ここより下は特集ページごとの固有の内容は無し

type PageContext = {
  tags: Tag[];
  articles: ArticleHeader[];
  isMobile: boolean;
  isDemoAccount: boolean;
  isAuthenticated: boolean;
  isLicenseVerified: boolean;
  isCorporationAccount: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  isPrivate: boolean;
};

const ClinicalReasoningPage: NextPage<PageContext> = ({
  tags,
  articles,
  isMobile,
  isDemoAccount,
  isAuthenticated,
  isLicenseVerified,
  isCorporationAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isPrivate,
}) => {
  const { specialPageTitle, specialPagePath, specialPageShortName } = setting;

  const { isBlocked, blockedContent, pageHead, breadcrumbs } = useSpecialPage({
    specialPageTitle,
    gaLabelKey: specialPageShortName,
    specialPagePath,
    isAuthenticated,
    isLicenseVerified,
    isCorporationAccount,
    isPublisherAccount,
    isDoctorRecruiterAccount,
    isDemoAccount,
    mainImagePath,
    isPrivate,
  });

  if (isBlocked) {
    return blockedContent;
  }

  return (
    <>
      {pageHead}
      <Box pt={[14, 14, 14, 16]} bg="white">
        {breadcrumbs}
        <FullWidthBox
          bg={HeroContentBgColor}
          display="flex"
          justifyContent="center"
          flexDirection="column"
        >
          <Container>{title}</Container>
        </FullWidthBox>
        <FullWidthBox
          bg="gray.100"
          display="flex"
          justifyContent="center"
          flexDirection="column"
        >
          <Container>{description}</Container>
        </FullWidthBox>
        <Box p={4}>
          {articles.length > 0 && (
            <>
              <Flex justifyContent="space-between" mb={1}>
                <Text fontWeight="bold" fontSize="md">
                  {`${tagsString(tags)} に関するスライド`}
                </Text>
              </Flex>
              <ArticlesGrid
                articles={articles}
                isMobile={isMobile}
                gaEventLabel={`${specialPageShortName}_tagsearch`}
                onShowThumbnail={(articleId) => {
                  sendAnalyticsEvent(
                    'slide_thumbnail',
                    'show',
                    `special_${specialPageShortName}#${articleId}`,
                  );
                }}
                onClick={(articleId) => {
                  sendAnalyticsEvent(
                    'slide_thumbnail',
                    'click',
                    `special_${specialPageShortName}#${articleId}`,
                  );
                }}
              />
            </>
          )}
        </Box>
        {/* 今後の内容が未定のため非表示にする必要がある箇所のみコメントアウト */}
        {/* <Box p={4}> */}
        {/*   <Box py={2}>{bannerListTitle}</Box> */}
        {/*   {banners} */}
        {/* </Box> */}
      </Box>
    </>
  );
};

export default ClinicalReasoningPage;

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';

    createUserAccessHistory({
      userId,
      userAgent,
      gaCid: getGaCid(context.req),
      operation: userAccessHistoryOperation,
    });

    const { tagTexts } = setting;

    const { isPrivate, canDisplay } = await getSpecialArticleProps(
      specialPageSlug,
      userId,
    );
    if (!canDisplay) {
      return {
        notFound: true,
      };
    }

    let articles: ArticleHeader[] = [];
    const targetTags = await findTagsByNames(tagTexts);
    const tags: Tag[] = targetTags.map((tag) => ({
      id: tag.tag_id,
      name: tag.tag_name,
    }));
    if (tags.length) {
      const articleIdList: string[] = await getArticleIdsWithTagIds(
        tags.map((tag) => tag.id),
      );

      const articleHeaders = await prisma.article_header.findMany({
        select: {
          article_id: true,
          name: true,
          article_type: true,
          status: true,
          top_img_m: true,
          session_count: true,
          like_count: true,
          disclose_scope: true,
          create_date: true,
          update_date: true,
          user_article_header_author_idTouser: true,
          article_tag_connection: {
            select: {
              tag: true,
            },
          },
          article_page: {
            select: {
              like_count: true,
            },
          },
          article_comments: {
            select: {
              id: true,
            },
          },
        },
        where: {
          article_id: {
            in: articleIdList,
          },
          status: 'DONE',
          hidden: false,
          is_deleted: false,
        },
        orderBy: {
          create_date: 'desc',
        },
      });
      // 投稿者がアカウント統合済みであればID基盤側のプロフィール情報を表示させる
      const discloseIds = articleHeaders
        .map((v) => v.user_article_header_author_idTouser.disclose_id ?? '')
        .filter((v) => !!v);
      const idBaseProfileMap = await fetchAccountIntegrationProfiles(
        discloseIds,
      );

      articles = articleHeaders.reduce((articles: any, article: any) => {
        const author = article.user_article_header_author_idTouser;
        const idBaseProfile = idBaseProfileMap[author.disclose_id ?? ''];

        if (
          shouldNotShow(
            idBaseProfile,
            context.req.user,
            context.req.isAuthenticated(),
            article,
          )
        ) {
          return articles;
        }

        articles.push({
          articleId: article.article_id,
          name: article.name,
          articleType: article.article_type,
          status: article.status as ArticleStatus | null,
          topImageM: article.top_img_m,
          sessionCount: article.session_count || 0,
          likeCount:
            article.like_count +
            article.article_page.reduce(
              (prev: any, curr: any) => prev + curr.like_count,
              0,
            ),
          commentCount: article.article_comments.length,
          discloseScope: article.disclose_scope as DiscloseScope | null,
          createDate: date.toIso8601Jst(article.create_date),
          updateDate: date.toIso8601Jst(article.update_date),
          author: {
            userId: article.user_article_header_author_idTouser.user_id,
            userName: idBaseProfile?.userName ?? null,
            // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
            lastName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.lastName ?? author.last_name,
            firstName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.firstName ?? author.first_name,
            updateDateEpochMilliseconds:
              article.user_article_header_author_idTouser.update_date.getTime(),
            profilePhotoS3Key:
              article.user_article_header_author_idTouser.profile_photo_s3_key,
            idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
            antaaAccountType: idBaseProfile?.accountType ?? null,
            antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
            isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
          },
          tags: article.article_tag_connection.map((conn: any) => ({
            id: conn.tag.tag_id,
            name: conn.tag.tag_name,
          })),
        });
        return articles;
      }, []);
    }

    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;
    const isDemoAccount = hasDemoAuthority(context.req.user?.userRoles);

    return {
      props: {
        tags,
        articles,
        isMobile,
        isDemoAccount,
        isAuthenticated: context.req.isAuthenticated(),
        isLicenseVerified: isLicenseVerified(context.req.user ?? null),
        isCorporationAccount:
          context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION &&
          context.req.user.discloseId !== 'llibLs1rfind', // 一時的に武田社のuser_idでアクセスされた場合のみ例外的に表示可能としている,
        isPublisherAccount: isPublisherAccount(context.req.user),
        isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
        isPrivate,
      },
    };
  },
);
